import {
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';

@Component({
  selector: 'app-dropdown-input',
  templateUrl: './dropdown-input.component.html',
  styleUrls: ['./dropdown-input.component.scss']
})
export class DropdownInputComponent implements OnInit, OnChanges {
  @Output() selectValueChanged = new EventEmitter();
  @ViewChild('input') input: ElementRef;
  @ViewChild('dropdownParent') dropdownParent: ElementRef;

  @Input() options = Array<{ value: string, text: string, meta?: any }>();
  @Input() selectedValue = '';
  @Input() placeholder = '';
  @Input() name: string;
  @Input() parentForm = new UntypedFormGroup({});
  @Input() className = '';
  @Input() disabled = false;
  @Input() loading = false;
  @Input() canClear = false;
  @Input() truncate = false;
  @Input() slim = false;
  @Input() filterable = false;

  selectedValueText = '';
  isFocused = false;
  hasPlaceholder = false;
  filterText = '';

  @HostListener('document:click', ['$event'])
  clickout(event) {
    if (!(this.input.nativeElement.contains(event.target) || this.dropdownParent.nativeElement.contains(event.target))) {
      this.isFocused = false;
    }
  }

  ngOnInit(): void {
    Promise.resolve().then(() => {
      this.updateSelectedValueText();
      this.syncDisabledState();
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['disabled']) {
      this.syncDisabledState();
    }
    if (changes['selectedValue'] || changes['options']) {
      this.updateSelectedValueText();
    }
  }

  private updateSelectedValueText(): void {
    if (!this.selectedValue) {
      this.setPlaceholder();
      return;
    }
    const option = this.options.find(o => o.value === this.selectedValue);
    if (option) {
      this.selectedValueText = option.text;
      this.hasPlaceholder = false;
    } else {
      this.setPlaceholder();
    }
  }

  private syncDisabledState(): void {
    if (!this.name || !this.parentForm) {
      return;
    }
    const control = this.parentForm.get(this.name);
    if (!control) {
      return;
    }
    if (this.disabled && !control.disabled) {
      control.disable({ emitEvent: false });
    } else if (!this.disabled && control.disabled) {
      control.enable({ emitEvent: false });
    }
  }

  reset() {
    this.selectedValue = '';
    this.setPlaceholder();

    if (this.name && this.parentForm) {
      this.parentForm.patchValue({ [this.name]: null }, { emitEvent: true });
      this.parentForm.markAsDirty();
    }

    this.selectValueChanged.emit(null);
  }

  setPlaceholder() {
    this.hasPlaceholder = true;
    this.selectedValueText = this.placeholder;
  }

  onItemSelect(index: number) {
    const option = this.options[index];
    this.selectedValue = option.value;
    this.selectedValueText = option.text;
    this.hasPlaceholder = false;
    this.input.nativeElement.value = option.value;
    this.onClose();

    this.parentForm.markAsDirty();
    this.parentForm.patchValue({ [this.name]: option.value }, { emitEvent: true });

    this.selectValueChanged.emit(option.value);
  }

  setValue(value: string) {
    let i = 0;


    for (const option of this.options) {
      if (option.value?.toLowerCase() === value?.toLowerCase()) {
        this.onItemSelect(i);
      }
      i++;
    }
  }

  get filteredOptions() {
    if (!this.filterable || !this.filterText) {
      return this.options;
    }
    const search = this.filterText.toLowerCase();
    return this.options.filter(o => o.text.toLowerCase().includes(search));
  }

  onFilterInput(event: Event) {
    this.filterText = (event.target as HTMLInputElement).value;
  }

  clearFilter() {
    this.filterText = '';
  }

  onFilteredItemSelect(filteredIndex: number) {
    const filteredOption = this.filteredOptions[filteredIndex];
    const originalIndex = this.options.indexOf(filteredOption);
    this.onItemSelect(originalIndex);
  }

  onBlur() {
    this.isFocused = false;
  }

  onOpen() {
    if (!this.disabled) {
      this.isFocused = !this.isFocused;
      if (this.isFocused) {
        this.filterText = '';
      }
    }
  }

  onClose() {
    if (!this.disabled) {
      this.isFocused = false;
      this.filterText = '';
    }
  }
}
